import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { indicesInfoCompl } from "@/lib/infoCompl";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useItem } from "@/contexts/ItemContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useModuloDeImpressao } from "@/components/ModuloDeImpressaoComponent";

import type { ImpressaoItemResponse } from "@/types/Impressao-item-response";
import {
  converterListaZplItemParaUrl,
  formatarData,
} from "@/utils/UtilFuncoes";
import {
  AlertCircle,
  ExternalLink,
  Eye,
  ImageOff,
  Loader2,
  MapPin,
  Package,
  Tag,
} from "lucide-react";

interface VerificarEtiquetaModalProps {
  /** Mantido pra compatibilidade — controle real vem do contexto */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function VerificarEtiquetaModal(_props: VerificarEtiquetaModalProps) {
  const { t } = useTranslation();
  const { coletaZplPadrao, impressaoConfig } = useImpressaoConfig();
  const { categorias } = useCategorias();
  const { localizacoes } = useLocalizacoes();
  const { pesquisarItensNaBase } = useItem();
  const { contaConfig } = useContaConfig();
  const { conferirEtiquetaOpen, setConferirEtiquetaOpen, epcConferido } =
    useModuloDeImpressao();

  const [itemSelecionado, setItemSelecionado] = useState<
    ImpressaoItemResponse | undefined
  >();
  const [buscando, setBuscando] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErro, setPreviewErro] = useState(false);

  // Buscar item na base sempre que abrir o modal com um EPC novo
  useEffect(() => {
    if (!conferirEtiquetaOpen || !epcConferido) {
      return;
    }
    let cancelado = false;
    const buscar = async () => {
      setBuscando(true);
      setItemSelecionado(undefined);
      try {
        // Normaliza pra uppercase — base costuma armazenar em uppercase
        const tag = epcConferido.trim().toUpperCase();
        // OBS: o backend devolve id_categoria e id_nivel_loc_* como
        // strings (sem populate). Resolvemos os nomes via lookup local
        // nos contexts (categorias / localizacoes) — sem precisar de query.
        const item = await pesquisarItensNaBase([tag]);
        if (cancelado) return;
        if (item && item.length > 0) {
          setItemSelecionado(item[0]);
        } else {
          setItemSelecionado(undefined);
        }
      } finally {
        if (!cancelado) setBuscando(false);
      }
    };
    buscar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferirEtiquetaOpen, epcConferido]);

  const handleClose = () => {
    setItemSelecionado(undefined);
    setConferirEtiquetaOpen(false);
  };

  // Backend devolve referências como strings (sem populate). Aceita string ou
  // objeto populado e extrai o _id pra fazer lookup nos contexts locais.
  const extrairId = (ref: unknown): string => {
    if (!ref) return "";
    if (typeof ref === "string") return ref;
    return (ref as any)?._id || "";
  };

  const idLoc1 = extrairId(itemSelecionado?.id_nivel_loc_1);
  const idLoc2 = extrairId(itemSelecionado?.id_nivel_loc_2);
  const idLoc3 = extrairId(itemSelecionado?.id_nivel_loc_3);
  const idLoc4 = extrairId(itemSelecionado?.id_nivel_loc_4);
  const idCategoria = extrairId(itemSelecionado?.id_categoria);

  const nomeLoc1 = idLoc1
    ? localizacoes.find((l) => l._id === idLoc1)?.descricao || ""
    : "";
  const nomeLoc2 = idLoc2
    ? localizacoes
        .flatMap((l) => l.children)
        .find((l2) => l2._id === idLoc2)?.descricao || ""
    : "";
  const nomeLoc3 = idLoc3
    ? localizacoes
        .flatMap((l) => l.children)
        .flatMap((l2) => l2.children)
        .find((l3) => l3._id === idLoc3)?.descricao || ""
    : "";
  const nomeLoc4 = idLoc4
    ? localizacoes
        .flatMap((l) => l.children)
        .flatMap((l2) => l2.children)
        .flatMap((l3) => l3.children)
        .find((l4) => l4._id === idLoc4)?.descricao || ""
    : "";

  const categoria = idCategoria
    ? categorias.find((c) => c._id === idCategoria)
    : undefined;

  const descricaoItem =
    categoria?.descricao ||
    (itemSelecionado?.id_categoria as any)?.descricao ||
    "";

  // Se o item não tem inf_compl_X preenchido, faz fallback pra categoria
  // (cadastrada uma vez) — assim aparece o que foi definido lá.
  const getInfoCompl = (n: number): string => {
    const fromItem = (itemSelecionado as any)?.[`inf_compl_${n}`];
    if (fromItem) return fromItem;
    return (categoria as any)?.[`inf_compl_${n}`] || "";
  };

  // URL da pré-visualização — gerada se o item tiver zpl_impressao
  const previewUrl = useMemo(() => {
    if (!itemSelecionado?.zpl_impressao) return null;
    const zplPadrao = coletaZplPadrao();
    if (!zplPadrao) return null;
    return converterListaZplItemParaUrl({
      ...zplPadrao,
      zpl: itemSelecionado.zpl_impressao,
    });
  }, [itemSelecionado, coletaZplPadrao]);

  useEffect(() => {
    if (previewUrl) {
      setPreviewLoading(true);
      setPreviewErro(false);
    }
  }, [previewUrl]);

  // helper pra renderizar campo vazio com "—"
  const fallback = (v: any) => (v === undefined || v === null || v === "" ? "—" : String(v));

  return (
    <Dialog open={conferirEtiquetaOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Tag className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base">
                {t("verificar.labelRead")}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {t("verificar.labelReadDesc")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Loading: buscando na base */}
        {buscando && (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">{t("verificar.searching")}</p>
          </div>
        )}

        {/* Não encontrado */}
        {!buscando && !itemSelecionado && epcConferido && (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <AlertCircle className="h-6 w-6 text-amber-500" />
            </div>
            <div className="text-center max-w-sm">
              <p className="text-sm font-medium text-foreground">
                {t("verificar.notFound")}
              </p>
              <p className="text-xs">{t("verificar.notFoundDesc")}</p>
            </div>
            <div className="text-xs rounded-md border bg-muted/30 px-3 py-1.5">
              {t("verificar.epcRead")}{" "}
              <span className="text-foreground">{epcConferido}</span>
            </div>
          </div>
        )}

        {/* Encontrado */}
        {!buscando && itemSelecionado && (
          <div className="space-y-4">
            {/* Hero da etiqueta */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 shrink-0">
                    <Tag className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      EPC
                    </p>
                    <p className="text-sm font-medium truncate">
                      {itemSelecionado.tag}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 sm:justify-end">
                  {itemSelecionado.quantidade && (
                    <Badge variant="secondary" className="font-normal">
                      {t("verificar.qty")} {itemSelecionado.quantidade}
                    </Badge>
                  )}
                  {itemSelecionado.valor > 0 && (
                    <Badge variant="secondary" className="font-normal">
                      R$ {itemSelecionado.valor.toFixed(2)}
                    </Badge>
                  )}
                  {itemSelecionado.dt_validade && (
                    <Badge variant="secondary" className="font-normal">
                      {t("verificar.val")}{" "}
                      {formatarData(itemSelecionado.dt_validade, false)}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Layout dinâmico: 2 cols quando tem preview, 1 col quando não tem */}
            <div
              className={
                previewUrl
                  ? "grid grid-cols-1 lg:grid-cols-5 gap-4"
                  : "space-y-4"
              }
            >
              <div className={previewUrl ? "lg:col-span-3 space-y-4" : "space-y-4"}>
                {/* Localizações */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm font-medium">
                        {t("verificar.locations")}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { label: t("verificar.level1"), value: nomeLoc1 },
                        { label: t("verificar.level2"), value: nomeLoc2 },
                        { label: t("verificar.level3"), value: nomeLoc3 },
                        { label: t("verificar.level4"), value: nomeLoc4 },
                      ].map((loc, i) => (
                        <div key={i} className="grid gap-2">
                          <Label className="text-xs text-muted-foreground">
                            {loc.label}
                          </Label>
                          <Input disabled type="text" value={fallback(loc.value)} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Dados do item */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm font-medium">
                        {t("verificar.itemData")}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-2">
                      <Label className="text-xs text-muted-foreground">
                        {t("verificar.description")}
                      </Label>
                      <Input
                        disabled
                        type="text"
                        value={fallback(descricaoItem)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {indicesInfoCompl(impressaoConfig).map((i) => {
                        const label =
                          (contaConfig as any)?.[`info_compl_${i}`] ||
                          t("verificar.infoCompl", { n: i });
                        const valor = getInfoCompl(i);
                        // Mostra só os 4 primeiros sempre; do 5 em diante só
                        // se tiver valor (pra não poluir com campos vazios)
                        if (i > 4 && !valor) return null;
                        return (
                          <div key={i} className="grid gap-2">
                            <Label className="text-xs text-muted-foreground">
                              {label}
                            </Label>
                            <Input
                              disabled
                              type="text"
                              value={fallback(valor)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Preview da etiqueta (visível direto, não em hover) */}
              {previewUrl && (
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <CardTitle className="text-sm font-medium">
                            {t("verificar.preview")}
                          </CardTitle>
                        </div>
                        <a
                          href={previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t("verificar.open")}
                        </a>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="relative rounded-md border bg-white overflow-hidden aspect-[3/4] flex items-center justify-center">
                        <img
                          key={previewUrl}
                          src={previewUrl}
                          alt={t("verificar.previewAlt")}
                          className="max-w-full max-h-full object-contain"
                          onLoad={() => {
                            setPreviewLoading(false);
                            setPreviewErro(false);
                          }}
                          onError={() => {
                            setPreviewLoading(false);
                            setPreviewErro(true);
                          }}
                        />
                        {previewLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {previewErro && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white text-muted-foreground p-4 text-center">
                            <ImageOff className="h-7 w-7 opacity-50" />
                            <p className="text-[11px]">
                              {t("verificar.renderError")}
                            </p>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground text-center mt-1">
                        {t("verificar.renderedBy")}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
